import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  Fab,
  Tooltip,
  Paper,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  CallSplit as SplitterIcon,
  Router as OltIcon,
  ImportExport as DioIcon,
  CenterFocusStrong as CenterIcon,
  Refresh as RefreshIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import ReactFlow, {
  Background,
  Controls,  
  Node,
  Edge,
  Connection,
  addEdge,
  useNodesState,
  useEdgesState,
  ReactFlowProvider,
  useReactFlow,
  Panel
} from 'reactflow';
import 'reactflow/dist/style.css';

import { splitterService, ReactFlowNode } from '../../services/splitterService';
import { FtthArmario } from '../../services/ftthArmarioService';
import { Cabo, caboService } from '../../services/caboService';
import type { PadraoTuboLoose } from '../../services/caboService';
import SplitterNode from '../flow/nodes/SplitterNode';
import CaboNode from '../flow/nodes/CaboNode';
import CaboPassagemNode from '../flow/nodes/CaboPassagemNode';
import OLTNode from '../flow/nodes/OLTNode';
import DIONode from '../flow/nodes/DIONode';
import CaboSelectionModal from './CaboSelectionModal';
import CabosForm from '../forms/FtthCabosForm';
import DioAddModal, { DioAddConfig } from './DioAddModal';
import DioEditPadraoModal from './DioEditPadraoModal';
import SplitterAddModal, { SplitterAddConfig } from './SplitterAddModal';
import SplitterEditPadraoModal, { SplitterEditConfig } from './SplitterEditPadraoModal';
import type { PadraoCoresSplitter } from '../../utils/fiberColors';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import '../../styles/swal-overrides.css';
import { oltService, OLT } from '../../services/oltService';
import DeletableEdge from '../flow/edges/DeletableEdge';
import BiDirectionalEdge from '../flow/edges/BiDirectionalEdge';
import { validateConnection, shouldUseBidirectionalEdge } from '../../utils/connectionValidation';
import { applySignalsToState, applySignalsToNodes, applySignalsToEdges } from '../../utils/diagramSignalsUtils';
import { exportDiagramImage } from '../../utils/exportDiagramImage';

const nodeTypes = {
  splitter: SplitterNode,
  caboIn: CaboNode,
  caboOut: CaboPassagemNode,
  olt: OLTNode,
  dio: DIONode,
};

const edgeTypes = {
  deletable: DeletableEdge,
  bidirectional: BiDirectionalEdge,
};

export interface ArmarioDiagramModalProps {
  open: boolean;
  onClose: () => void;
  armario: FtthArmario;
  isFullscreen?: boolean;
  container?: HTMLElement | null;
}

const ArmarioDiagramContent = React.memo<{
  armario: FtthArmario;
  onClose: () => void;
  isFullscreen?: boolean;
  /** Quando em tela cheia, container para o Menu aparecer dentro do elemento fullscreen */
  container?: HTMLElement | null;
}>(({ armario, onClose, isFullscreen = false, container }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [addMenuAnchor, setAddMenuAnchor] = useState<null | HTMLElement>(null);
  const [showCaboSelectionModal, setShowCaboSelectionModal] = useState(false);
  const [showOltSelectionModal, setShowOltSelectionModal] = useState(false);
  const [showCabosForm, setShowCabosForm] = useState(false);
  const [editingCabo, setEditingCabo] = useState<Cabo | null>(null);
  const [showDioModal, setShowDioModal] = useState(false);
  const [showDioEditModal, setShowDioEditModal] = useState(false);
  const [editingDio, setEditingDio] = useState<{
    id: number;
    padrao_cores: PadraoTuboLoose;
    qtde_portas: number;
  } | null>(null);
  const [showSplitterAddModal, setShowSplitterAddModal] = useState(false);
  const [showSplitterEditModal, setShowSplitterEditModal] = useState(false);
  const [editingSplitter, setEditingSplitter] = useState<{
    id: number;
    padrao_cores: PadraoCoresSplitter;
    config: string;
    is1x2: boolean;
    saida1_porcentagem: number;
    saida2_porcentagem: number;
  } | null>(null);
  const [oltsDisponiveis, setOltsDisponiveis] = useState<OLT[]>([]);
  const [loadingOlts, setLoadingOlts] = useState(false);
  const [selectedEdges, setSelectedEdges] = useState<string[]>([]);
  const [refreshingSignal, setRefreshingSignal] = useState(false);
  const [exportingImage, setExportingImage] = useState(false);
  const [flowInteractive, setFlowInteractive] = useState(false);

  const edgesRef = useRef(edges);
  const nodesRef = useRef(nodes);
  const loadingRef = useRef(false);
  const flowContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => { edgesRef.current = edges; }, [edges]);
  useEffect(() => { nodesRef.current = nodes; }, [nodes]);
  useEffect(() => { setFlowInteractive(false); }, [armario.id]);

  const reactFlowInstance = useReactFlow();

  /** Busca sinais ópticos e aplica aos nós/edges (único ponto de chamada a getOpticalSignals para atualização). */
  const refreshSignalsSilent = useCallback(async () => {
    const signalsData = await splitterService.getOpticalSignals(armario.id, 'armario');
    applySignalsToState(signalsData, setNodes, setEdges);
  }, [armario.id, setNodes, setEdges]);

  const handleDeleteConnection = useCallback(async (edgeId: string) => {
    try {
      const edgeToDelete = edgesRef.current.find(edge => edge.id === edgeId);
      if (!edgeToDelete) {
        toast.error('Conexão não encontrada');
        return;
      }
      let backendId: number;
      if (edgeToDelete.data?.id && typeof edgeToDelete.data.id === 'number') {
        backendId = edgeToDelete.data.id;
      } else {
        const numericId = edgeId.replace(/^.*-/, '');
        backendId = parseInt(numericId);
        if (isNaN(backendId) || backendId === 0) {
          toast.error('ID da conexão inválido.');
          return;
        }
      }
      await splitterService.deleteConnection(backendId);
      setEdges((eds) => eds.filter(edge => edge.id !== edgeId));
      toast.success('Conexão deletada com sucesso!');

      try {
        await refreshSignalsSilent();
      } catch (_err) {
      }
    } catch (error: any) {
      toast.error(`Erro ao deletar conexão: ${error.message || 'Erro desconhecido'}`);
    }
  }, [setEdges, refreshSignalsSilent]);

  const handleDeleteSplitter = useCallback(async (splitterId: number) => {
    try {
      const { hasSiblings, siblingIds } = await splitterService.getCaboSiblings(splitterId);
      let deletePair = false;

      if (hasSiblings && siblingIds.length > 0) {
        const result = await Swal.fire({
          title: 'Existe outro nó deste cabo',
          html: 'Há outro nó (entrada ou saída) do mesmo cabo neste armário. Deseja deletar apenas este ou ambos?<br/><small>Se deletar ambos, o vínculo com o ponto no mapa será removido.</small>',
          icon: 'question',
          showCancelButton: true,
          showDenyButton: true,
          confirmButtonText: 'Deletar apenas este',
          denyButtonText: 'Deletar ambos',
          cancelButtonText: 'Cancelar',
          target: (document.fullscreenElement ?? (document as any).webkitFullscreenElement ?? document.body) as HTMLElement,
          customClass: { container: 'swal-zindex', popup: 'swal-zindex' },
          didOpen: () => {
            const el = document.querySelector('.swal2-container') as HTMLElement;
            if (el) el.style.zIndex = '20001';
          }
        });
        if (result.isDismissed) return;
        if (result.isDenied) deletePair = true;
      } else {
        const confirmed = await Swal.fire({
          title: 'Confirmar exclusão',
          text: 'Tem certeza que deseja deletar este item? Esta ação não pode ser desfeita.',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Sim, deletar',
          cancelButtonText: 'Cancelar',
          target: (document.fullscreenElement ?? (document as any).webkitFullscreenElement ?? document.body) as HTMLElement,
          customClass: { container: 'swal-zindex', popup: 'swal-zindex' },
          didOpen: () => {
            const el = document.querySelector('.swal2-container') as HTMLElement;
            if (el) el.style.zIndex = '20001';
          }
        });
        if (!confirmed.isConfirmed) return;
      }

      await splitterService.deleteSplitter(splitterId, deletePair);
      const idsToRemove = deletePair ? [splitterId, ...siblingIds] : [splitterId];
      const idsSet = new Set(idsToRemove);

      const isEdgeConnectedToDeleted = (edge: { source: string; target: string }) => {
        const srcNum = parseInt(edge.source.replace(/\D/g, ''), 10);
        const tgtNum = parseInt(edge.target.replace(/\D/g, ''), 10);
        return idsSet.has(srcNum) || idsSet.has(tgtNum);
      };

      setNodes((n) => n.filter(node => !idsSet.has(node.data.id)));
      setEdges((e) => e.filter(edge => !isEdgeConnectedToDeleted(edge)));
      toast.success(deletePair ? 'Cabo removido e vínculo desfeito!' : 'Deletado com sucesso!');
    } catch (error: any) {
      toast.error(`Erro ao deletar splitter: ${error.message || 'Erro desconhecido'}`);
    }
  }, [setNodes, setEdges]);

  const handleUpdatePorcentagem = useCallback(async (splitterId: number, porcentagem: number) => {
    setNodes((n) => n.map(node =>
      node.data?.id === splitterId
        ? { ...node, data: { ...node.data, saida1_porcentagem: porcentagem, saida2_porcentagem: 100 - porcentagem } }
        : node
    ));
    try {
      const result = await splitterService.updateSaida1Porcentagem(splitterId, porcentagem);
      if (result) {
        setNodes((n) => n.map(node =>
          node.data?.id === splitterId
            ? { ...node, data: { ...node.data, ...result } }
            : node
        ));
      }
      toast.success(`Porcentagem atualizada: ${porcentagem}% / ${100 - porcentagem}%`);

      try {
        await refreshSignalsSilent();
      } catch (_err) {
      }
    } catch (error: any) {
      toast.warning(`Porcentagem atualizada localmente (${porcentagem}% / ${100 - porcentagem}%), mas houve erro ao salvar.`);
    }
  }, [setNodes, armario.id, refreshSignalsSilent]);

  const handleUpdateObservacao = useCallback(async (splitterId: number, observacao: string) => {
    setNodes((n) => n.map(node =>
      node.data?.id === splitterId ? { ...node, data: { ...node.data, observacao } } : node
    ));
    try {
      await splitterService.updateSplitterObservacao(splitterId, observacao);
      toast.success('Observação atualizada com sucesso!');
    } catch (error: any) {
      toast.warning('Observação atualizada localmente, mas houve erro ao salvar.');
    }
  }, [setNodes]);

  const handleUpdatePortaTipo = useCallback(async (splitterId: number, numeroFibra: number, tipo: 'in' | 'out') => {
    try {
      const { portas_tipo } = await splitterService.updatePortaTipo(splitterId, numeroFibra, tipo);
      setNodes((n) => n.map(node => {
        if (node.data?.id !== splitterId) return node;
        return { ...node, data: { ...node.data, portas_tipo } };
      }));
      toast.success('Tipo da porta atualizado.');
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao atualizar tipo da porta.');
    }
  }, [setNodes]);

  const handleEditCabo = useCallback(async (caboId: number) => {
    try {
      const cabo = await caboService.getCaboById(caboId);
      setEditingCabo(cabo);
      setShowCabosForm(true);
    } catch (error: any) {
      toast.error(`Erro ao carregar cabo: ${error.message || 'Erro desconhecido'}`);
    }
  }, []);

  const applyCaboUpdateToNodes = useCallback((cabo: Cabo) => {
    setNodes((currentNodes) => currentNodes.map((node) => {
      if (node.type !== 'caboIn' && node.type !== 'caboOut') return node;
      if (node.data?.cabo_id !== cabo.id) return node;

      const defaultTipoPorta = node.type === 'caboOut' ? 'in' : 'out';
      const existingPortas = (node.data.portas_tipo ?? {}) as Record<string, 'in' | 'out'>;
      const portas_tipo: Record<string, 'in' | 'out'> = {};
      for (let i = 1; i <= cabo.qtde_fo; i++) {
        portas_tipo[String(i)] = existingPortas[String(i)] ?? defaultTipoPorta;
      }

      return {
        ...node,
        data: {
          ...node.data,
          titulo: cabo.titulo,
          qtde_fo: cabo.qtde_fo,
          color: cabo.color,
          cabo_tipo: cabo.tipo ?? null,
          padrao_tubo_loose: cabo.padrao_tubo_loose ?? 'abnt',
          qtde_tubo_loose: cabo.qtde_tubo_loose ?? 1,
          portas_tipo,
        },
      };
    }));
  }, [setNodes]);

  const handleCabosFormSuccess = useCallback((cabo: Cabo) => {
    applyCaboUpdateToNodes(cabo);
    setShowCabosForm(false);
    setEditingCabo(null);
    toast.success('Cabo atualizado com sucesso!');
  }, [applyCaboUpdateToNodes]);

  const handleCabosFormCancel = useCallback(() => {
    setShowCabosForm(false);
    setEditingCabo(null);
  }, []);

  const handleEditDioPadrao = useCallback((splitterId: number) => {
    const node = nodesRef.current.find((n) => n.data?.id === splitterId && n.type === 'dio');
    if (!node) return;
    setEditingDio({
      id: splitterId,
      padrao_cores: (node.data.padrao_cores as PadraoTuboLoose) ?? 'abnt',
      qtde_portas: node.data.qtde_portas ?? node.data.outputs ?? 12,
    });
    setShowDioEditModal(true);
  }, []);

  const handleSaveDioPadrao = useCallback(async (padrao_cores: PadraoTuboLoose) => {
    if (!editingDio) return;
    try {
      await splitterService.updateSplitter(editingDio.id, { padrao_cores } as any);
      setNodes((current) => current.map((node) => {
        if (node.data?.id !== editingDio.id || node.type !== 'dio') return node;
        return { ...node, data: { ...node.data, padrao_cores } };
      }));
      setShowDioEditModal(false);
      setEditingDio(null);
      toast.success('Padrão de cores do DIO atualizado!');
    } catch (error: any) {
      toast.error(`Erro ao atualizar DIO: ${error.message || 'Erro desconhecido'}`);
    }
  }, [editingDio, setNodes]);

  const handleEditSplitterPadrao = useCallback((splitterId: number) => {
    const node = nodesRef.current.find((n) => n.data?.id === splitterId && n.type === 'splitter');
    if (!node) return;
    const outputs = typeof node.data.outputs === 'number' ? node.data.outputs : parseInt(node.data.outputs) || 2;
    setEditingSplitter({
      id: splitterId,
      padrao_cores: (node.data.padrao_cores as PadraoCoresSplitter) ?? 'sem_padrao',
      config: node.data.config ?? `1x${outputs}`,
      is1x2: outputs === 2,
      saida1_porcentagem: node.data.saida1_porcentagem ?? 50,
      saida2_porcentagem: node.data.saida2_porcentagem ?? 50,
    });
    setShowSplitterEditModal(true);
  }, []);

  const handleSaveSplitterPadrao = useCallback(async (data: SplitterEditConfig) => {
    if (!editingSplitter) return;
    try {
      await splitterService.updateSplitter(editingSplitter.id, { padrao_cores: data.padrao_cores } as any);

      let saida1 = data.saida1_porcentagem;
      let saida2 = data.saida2_porcentagem;

      if (editingSplitter.is1x2 && saida1 !== undefined) {
        const result = await splitterService.updateSaida1Porcentagem(editingSplitter.id, saida1);
        saida1 = result.saida1_porcentagem;
        saida2 = result.saida2_porcentagem;
      }

      setNodes((current) => current.map((node) => {
        if (node.data?.id !== editingSplitter.id || node.type !== 'splitter') return node;
        return {
          ...node,
          data: {
            ...node.data,
            padrao_cores: data.padrao_cores,
            ...(editingSplitter.is1x2 && saida1 !== undefined
              ? { saida1_porcentagem: saida1, saida2_porcentagem: saida2 }
              : {}),
          },
        };
      }));
      setShowSplitterEditModal(false);
      setEditingSplitter(null);
      toast.success('Splitter atualizado!');

      if (editingSplitter.is1x2 && data.saida1_porcentagem !== undefined) {
        try {
          await refreshSignalsSilent();
        } catch (_err) {
          // sinal será recalculado na próxima atualização manual
        }
      }
    } catch (error: any) {
      toast.error(`Erro ao atualizar splitter: ${error.message || 'Erro desconhecido'}`);
    }
  }, [editingSplitter, setNodes, refreshSignalsSilent]);

  const loadArmarioData = useCallback(async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    try {
      setLoading(true);
      setError('');
      const [nodesData, edgesData, signalsData] = await Promise.all([
        splitterService.getNodesForReactFlowArmario(armario.id),
        splitterService.getEdgesForReactFlowArmario(armario.id),
        splitterService.getOpticalSignals(armario.id, 'armario')
      ]);
      const baseNodes = (nodesData?.nodes || [])
        .filter((n: any) => n && n.data)
        .map((node: any) => ({
          ...node,
          data: {
            ...node.data,
            outputs: typeof node.data.outputs === 'number' ? node.data.outputs : parseInt(node.data.outputs) || 2,
            qtde_portas: node.data.qtde_portas ?? node.data.outputs,
            saida1_porcentagem: node.data.saida1_porcentagem || 50,
            saida2_porcentagem: node.data.saida2_porcentagem || 50,
            opticalSignal: null as any,
            onDelete: handleDeleteSplitter,
            onUpdatePorcentagem: handleUpdatePorcentagem,
            onUpdateObservacao: handleUpdateObservacao,
            onUpdatePortaTipo: handleUpdatePortaTipo,
            onEditCabo: handleEditCabo,
            onEditDioPadrao: handleEditDioPadrao,
            onEditSplitterPadrao: handleEditSplitterPadrao,
          }
        }));
      const processedNodes = applySignalsToNodes(baseNodes, signalsData);
      const baseEdges = (edgesData || []).map((edge: any) => ({
        ...edge,
        type: 'deletable',
        animated: false,
        data: { ...edge.data, onDelete: handleDeleteConnection }
      }));
      const processedEdges = applySignalsToEdges(baseEdges, signalsData);
      setNodes(processedNodes);
      setEdges(processedEdges);
      setTimeout(() => reactFlowInstance?.fitView({ padding: 0.2 }), 100);
    } catch (err: any) {
      setError(`Erro ao carregar diagrama: ${err.message || 'Erro desconhecido'}`);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [armario.id, handleDeleteSplitter, handleUpdatePorcentagem, handleUpdateObservacao, handleUpdatePortaTipo, handleEditCabo, handleEditDioPadrao, handleEditSplitterPadrao, handleDeleteConnection, reactFlowInstance, setNodes, setEdges]);

  useEffect(() => {
    if (armario.id && !loadingRef.current) loadArmarioData();
  }, [armario.id]);

  const onSelectionChange = useCallback((params: { nodes: Node[]; edges: Edge[] }) => {
    setSelectedEdges(params.edges.map(e => e.id));
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Delete' && selectedEdges.length > 0) {
        event.preventDefault();
        selectedEdges.forEach(handleDeleteConnection);
        setSelectedEdges([]);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedEdges, handleDeleteConnection]);

  const onConnect = useCallback(
    async (params: Connection) => {
      if (!params.source || !params.target) return;
      try {
        const edgeType = shouldUseBidirectionalEdge(params) ? 'bidirectional' : 'deletable';
        const edgeColor = '#9C27B0';
        const connectionData = {
          armario_id: armario.id,
          source: params.source,
          sourceHandle: params.sourceHandle ?? undefined,
          target: params.target,
          targetHandle: params.targetHandle ?? undefined,
          connection_label: '',
          style_color: edgeColor
        };
        const createdConnection = await splitterService.createConnection(connectionData);
        const newEdge = {
          id: `connection-${createdConnection.id}`,
          source: params.source,
          sourceHandle: params.sourceHandle ?? undefined,
          target: params.target,
          targetHandle: params.targetHandle ?? undefined,
          type: edgeType,
          animated: false,
          label: '',
          labelStyle: { fontSize: 11, fontWeight: 'bold' },
          style: { stroke: edgeColor, strokeWidth: 2 },
          data: { id: createdConnection.id, armario_id: armario.id, onDelete: handleDeleteConnection }
        };
        setEdges((eds) => [...eds, newEdge]);
        toast.success('Conexão criada com sucesso!');

        // Atualizar sinais na próxima microtask para não sobrescrever a edge recém-adicionada
        setTimeout(() => {
          refreshSignalsSilent().catch(() => {});
        }, 0);
      } catch (error: any) {
        toast.error(`Erro ao criar conexão: ${error.message || 'Erro desconhecido'}`);
      }
    },
    [armario.id, setEdges, handleDeleteConnection, refreshSignalsSilent]
  );

  const onNodeDragStop = useCallback(async (_event: React.MouseEvent, node: Node) => {
    try {
      let itemId: number | null = null;
      if (node.id.startsWith('splitter-')) itemId = parseInt(node.id.replace('splitter-', ''));
      else if (node.id.startsWith('cabo-')) itemId = parseInt(node.id.replace('cabo-', ''));
      else if (node.id.startsWith('olt-')) itemId = parseInt(node.id.replace('olt-', ''));
      else if (node.id.startsWith('dio-')) itemId = parseInt(node.id.replace('dio-', ''));

      if (itemId && !isNaN(itemId)) {
        await splitterService.updateSplitterPosition(itemId, node.position);
      }
    } catch (error: any) {
      toast.error(`Erro ao salvar posição: ${error.message}`);
    }
  }, []);

  const handleAddMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => setAddMenuAnchor(event.currentTarget), []);
  const handleAddMenuClose = useCallback(() => setAddMenuAnchor(null), []);

  const handleOpenSplitterModal = useCallback(() => {
    setShowSplitterAddModal(true);
    handleAddMenuClose();
  }, [handleAddMenuClose]);

  const handleCenterView = useCallback(() => reactFlowInstance?.fitView({ padding: 0.2 }), [reactFlowInstance]);

  /** Recalcula o sinal óptico das conexões e atualiza a animação dos cabos */
  const handleRefreshSignal = useCallback(async () => {
    if (refreshingSignal) return;
    setRefreshingSignal(true);
    try {
      await refreshSignalsSilent();
      toast.success('Sinal das conexões atualizado');
    } catch (_err: any) {
      toast.error('Erro ao recalcular sinal. Tente novamente.');
    } finally {
      setRefreshingSignal(false);
    }
  }, [refreshingSignal, refreshSignalsSilent]);

  const handleExportImage = useCallback(async () => {
    if (!reactFlowInstance || !flowContainerRef.current || exportingImage) return;
    setExportingImage(true);
    try {
      await exportDiagramImage(
        flowContainerRef.current,
        reactFlowInstance,
        `diagrama_armario_${armario.name}`,
      );
      toast.success('Imagem exportada com sucesso!');
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao exportar imagem');
    } finally {
      setExportingImage(false);
    }
  }, [reactFlowInstance, exportingImage, armario.name]);

  const createReactFlowNode = useCallback((splitter: any): ReactFlowNode => {
    let processedConfigSaidas = splitter.config_saidas;
    if (typeof splitter.config_saidas === 'string') {
      try { processedConfigSaidas = JSON.parse(splitter.config_saidas); } catch { processedConfigSaidas = null; }
    }
    let outputs = 2;
    if (typeof processedConfigSaidas === 'object' && processedConfigSaidas !== null) {
      const saidaKeys = Object.keys(processedConfigSaidas).filter((k) => /^saida\d+$/.test(k));
      outputs = saidaKeys.length > 0 ? saidaKeys.length : 2;
    } else if (typeof processedConfigSaidas === 'number') {
      outputs = processedConfigSaidas;
    }
    const is1x2 = outputs === 2;
    let padraoCores: PadraoCoresSplitter = 'sem_padrao';
    if (typeof processedConfigSaidas === 'object' && processedConfigSaidas !== null) {
      const p = (processedConfigSaidas as { padrao_cores?: PadraoCoresSplitter }).padrao_cores;
      if (p === 'abnt' || p === 'eia598a' || p === 'sem_padrao') padraoCores = p;
    }
    const baseData = {
      id: splitter.id,
      label: `Splitter ${splitter.id}`,
      tipo: splitter.tipo,
      config: `1x${outputs}`,
      color: '#9C27B0',
      inputs: 1,
      outputs,
      padrao_cores: (splitter as any).padrao_cores ?? padraoCores,
      onDelete: handleDeleteSplitter,
      onUpdatePorcentagem: handleUpdatePorcentagem,
      onEditSplitterPadrao: handleEditSplitterPadrao,
    };
    if (is1x2 && typeof processedConfigSaidas === 'object' && processedConfigSaidas?.saida1?.valor !== undefined) {
      return {
        id: `splitter-${splitter.id}`,
        type: 'splitter',
        position: { x: splitter.posicao_x, y: splitter.posicao_y },
        data: { ...baseData, saida1_porcentagem: processedConfigSaidas.saida1.valor, saida2_porcentagem: processedConfigSaidas.saida2.valor } as any
      };
    }
    return {
      id: `splitter-${splitter.id}`,
      type: 'splitter',
      position: { x: splitter.posicao_x, y: splitter.posicao_y },
      data: baseData as any
    };
  }, [handleDeleteSplitter, handleUpdatePorcentagem, handleEditSplitterPadrao]);

  const handleAddSplitter = useCallback(async ({ saidas, padrao_cores, saida1_porcentagem, saida2_porcentagem }: SplitterAddConfig) => {
    try {
      const splitterData: Record<string, unknown> = {
        armario_id: armario.id,
        tipo: 'splitter',
        posicao_x: 200,
        posicao_y: 200,
        config_saidas: saidas,
        padrao_cores,
      };
      if (saidas === 2) {
        splitterData.saida1_porcentagem = saida1_porcentagem;
        splitterData.saida2_porcentagem = saida2_porcentagem;
      }
      const newSplitter = await splitterService.createSplitter(splitterData as any);
      const newNode = createReactFlowNode(newSplitter);
      setNodes((n) => [...n, newNode]);
      toast.success(`Splitter 1x${saidas} adicionado!`);
    } catch (error: any) {
      toast.error(`Erro ao adicionar splitter: ${error.message}`);
    } finally {
      setShowSplitterAddModal(false);
    }
  }, [armario.id, createReactFlowNode, setNodes]);

  const handleAddDio = useCallback(async ({ portas, padrao_cores }: DioAddConfig) => {
    try {
      const dioData = {
        armario_id: armario.id,
        tipo: 'dio',
        posicao_x: 250,
        posicao_y: 200,
        config_saidas: portas,
        padrao_cores,
      };
      const newDio = await splitterService.createSplitter(dioData);
      const cfg = (newDio as any).config_saidas;
      const portasCount = typeof cfg === 'object' && cfg?.portas != null
        ? cfg.portas
        : (typeof cfg === 'number' ? cfg : portas);
      const padrao = typeof cfg === 'object' && cfg?.padrao_cores
        ? cfg.padrao_cores
        : padrao_cores;
      const newNode = {
        id: `dio-${(newDio as any).id}`,
        type: 'dio',
        position: { x: (newDio as any).posicao_x, y: (newDio as any).posicao_y },
        data: {
          id: (newDio as any).id,
          label: `DIO ${portasCount}p`,
          tipo: 'dio',
          config: `${portasCount} portas`,
          color: '#FF5722',
          outputs: portasCount,
          qtde_portas: portasCount,
          padrao_cores: padrao,
          onDelete: handleDeleteSplitter,
          onEditDioPadrao: handleEditDioPadrao,
        },
      };
      setNodes((n) => [...n, newNode]);
      setShowDioModal(false);
      toast.success(`DIO de ${portasCount} portas adicionado!`);
    } catch (error: any) {
      toast.error(`Erro ao adicionar DIO: ${error.message}`);
    } finally {
      handleAddMenuClose();
    }
  }, [armario.id, handleDeleteSplitter, handleEditDioPadrao, setNodes, handleAddMenuClose]);

  const handleOpenDioModal = useCallback(() => {
    setShowDioModal(true);
    handleAddMenuClose();
  }, [handleAddMenuClose]);

  const adicionarCaboNormal = useCallback(async (caboSelecionado: Cabo, tipo: string, posX: number, posY: number) => {
    const caboData = {
      armario_id: armario.id,
      tipo,
      cabo_id: caboSelecionado.id,
      posicao_x: posX,
      posicao_y: posY
    };
    const newCabo = await splitterService.createSplitter(caboData);
    const qtdeFo = ((newCabo as any).cabo?.qtde_fo) ?? caboSelecionado.qtde_fo ?? 1;
    const defaultTipoPorta = tipo === 'caboOut' ? 'in' : 'out';
    const cfg = (newCabo as any).config_saidas;
    let portas_tipo: Record<string, 'in' | 'out'> = {};
    if (typeof cfg === 'object' && cfg !== null && Object.keys(cfg).some((k: string) => /^fo\d+$/.test(k))) {
      for (let i = 1; i <= qtdeFo; i++) {
        const v = (cfg as any)[`fo${i}`];
        portas_tipo[String(i)] = (v?.tipo === 'in' || v?.tipo === 'out') ? v.tipo : defaultTipoPorta;
      }
    } else {
      for (let i = 1; i <= qtdeFo; i++) portas_tipo[String(i)] = defaultTipoPorta;
    }
    const newNode = {
      id: `cabo-${(newCabo as any).id}`,
      type: tipo === 'caboOut' ? 'caboOut' : 'caboIn',
      position: { x: (newCabo as any).posicao_x, y: (newCabo as any).posicao_y },
      data: {
        id: (newCabo as any).id,
        cabo_id: (newCabo as any).cabo_id || (newCabo as any).id,
        titulo: ((newCabo as any).cabo?.titulo) || caboSelecionado.titulo,
        qtde_fo: qtdeFo,
        color: ((newCabo as any).cabo?.color) || caboSelecionado.color,
        cabo_tipo: ((newCabo as any).cabo?.tipo) || caboSelecionado.tipo || null,
        padrao_tubo_loose: ((newCabo as any).cabo?.padrao_tubo_loose) || caboSelecionado.padrao_tubo_loose || 'abnt',
        qtde_tubo_loose: ((newCabo as any).cabo?.qtde_tubo_loose) ?? caboSelecionado.qtde_tubo_loose ?? 1,
        tipo: (newCabo as any).tipo,
        observacao: (newCabo as any).observacao,
        portas_tipo,
        isPassagem: tipo === 'caboOut',
        onDelete: handleDeleteSplitter,
        onUpdateObservacao: handleUpdateObservacao,
        onUpdatePortaTipo: handleUpdatePortaTipo,
        onEditCabo: handleEditCabo,
      }
    };
    setNodes((n) => [...n, newNode]);
  }, [armario.id, handleDeleteSplitter, handleUpdateObservacao, handleUpdatePortaTipo, handleEditCabo, setNodes]);

  const adicionarCaboComPassagem = useCallback(async (caboSelecionado: Cabo) => {
    await adicionarCaboNormal(caboSelecionado, 'caboIn', 250, 200);
    await adicionarCaboNormal(caboSelecionado, 'caboOut', 350, 200);
    toast.success(`Cabo "${caboSelecionado.titulo}" adicionado ao diagrama!`);
  }, [adicionarCaboNormal]);

  const handleOpenCaboSelection = useCallback(() => {
    setShowCaboSelectionModal(true);
    handleAddMenuClose();
  }, [handleAddMenuClose]);

  const handleOpenOltSelection = useCallback(async () => {
    handleAddMenuClose();
    setLoadingOlts(true);
    try {
      const res = await oltService.getAll({ ativo: true, armario_id: 'null' });
      const allOlts = res.olts || res.data?.olts || [];

      // Obter IDs das OLTs que já estão desenhadas no diagrama
      const addedOltIds = new Set(
        nodes
          .filter(node => node.type === 'olt' && node.data?.olt_id)
          .map(node => node.data.olt_id)
      );

      // Filtrar para mostrar apenas as OLTs que ainda não foram adicionadas
      const availableOlts = allOlts.filter((olt: OLT) => !addedOltIds.has(olt.id));

      setOltsDisponiveis(availableOlts);
      setShowOltSelectionModal(true);
    } catch (error: any) {
      toast.error(`Erro ao buscar OLTs: ${error.message}`);
    } finally {
      setLoadingOlts(false);
    }
  }, [handleAddMenuClose, nodes]);

  const handleAddOlt = useCallback(async (olt: OLT) => {
    try {
      const oltData = {
        armario_id: armario.id,
        tipo: 'olt',
        olt_id: olt.id,
        posicao_x: 100,
        posicao_y: 200,
        config_saidas: 1
      };
      const newOlt = await splitterService.createSplitter(oltData);
      const newNode = {
        id: `olt-${(newOlt as any).id}`,
        type: 'olt',
        position: { x: (newOlt as any).posicao_x, y: (newOlt as any).posicao_y },
        data: {
          id: (newOlt as any).id,
          olt_id: olt.id,
          oltName: olt.name,
          oltVendor: olt.vendor,
          oltModel: olt.model,
          onDelete: handleDeleteSplitter
        }
      };
      setNodes((n) => [...n, newNode]);
      setShowOltSelectionModal(false);
      toast.success(`OLT "${olt.name}" adicionada ao diagrama!`);
    } catch (error: any) {
      toast.error(`Erro ao adicionar OLT: ${error.message}`);
    }
  }, [armario.id, handleDeleteSplitter, setNodes]);

  const handleAddCabo = useCallback(async (caboSelecionado: Cabo, tipoCabo: string = 'normal') => {
    try {
      if (tipoCabo === 'passagem') {
        await adicionarCaboComPassagem(caboSelecionado);
      } else if (tipoCabo === 'saida') {
        await adicionarCaboNormal(caboSelecionado, 'caboOut', 300, 200);
        toast.success(`Cabo de saída "${caboSelecionado.titulo}" adicionado!`);
      } else {
        await adicionarCaboNormal(caboSelecionado, 'caboIn', 300, 200);
        toast.success(`Cabo "${caboSelecionado.titulo}" adicionado!`);
      }
    } catch (error: any) {
      toast.error(`Erro ao adicionar cabo: ${error.message}`);
    }
  }, [adicionarCaboNormal, adicionarCaboComPassagem]);

  if (loading) {
    return (
      <Box sx={{ minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
        <CircularProgress size={40} />
        <Typography variant="body2">Carregando diagrama...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ mb: 1, py: 1 }}>{error}</Alert>
        <Button variant="outlined" size="small" onClick={loadArmarioData}>Tentar Novamente</Button>
      </Box>
    );
  }

  return (
    <Box ref={flowContainerRef} sx={{ height: '100%', minHeight: 400, width: '100%', position: 'relative' }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeDragStop={onNodeDragStop}
        isValidConnection={(c) => validateConnection(c, edges)}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        nodesDraggable={flowInteractive}
        nodesConnectable={flowInteractive}
        elementsSelectable={flowInteractive}
        edgesUpdatable={flowInteractive}
        attributionPosition="bottom-left"
        proOptions={{ hideAttribution: true }}
        onSelectionChange={onSelectionChange}
      >
        <Background />
        <Controls showFitView={false} onInteractiveChange={setFlowInteractive} />
        <Panel position="top-right">
          <Button
            variant="contained"
            size="small"
            color="primary"
            startIcon={exportingImage ? <CircularProgress size={16} color="inherit" /> : <ImageIcon />}
            onClick={handleExportImage}
            disabled={exportingImage || nodes.length === 0}
            sx={{ boxShadow: 2, textTransform: 'none', fontWeight: 600 }}
          >
            {exportingImage ? 'Exportando...' : 'Exportar imagem'}
          </Button>
        </Panel>
        <Panel position="top-left">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Tooltip
              title={flowInteractive ? 'Adicionar Componente' : 'Desbloqueie o diagrama para adicionar componentes'}
              placement="right"
            >
              <span>
                <Fab
                  size="small"
                  color="secondary"
                  onClick={handleAddMenuOpen}
                  disabled={!flowInteractive}
                >
                  <AddIcon />
                </Fab>
              </span>
            </Tooltip>
            <Tooltip title="Centralizar Diagrama" placement="right">
              <Fab size="small" onClick={handleCenterView}><CenterIcon /></Fab>
            </Tooltip>
            <Tooltip title="Recalcular sinal das conexões" placement="right">
              <span>
                <Fab
                  size="small"
                  color="default"
                  onClick={handleRefreshSignal}
                  disabled={refreshingSignal}
                >
                  {refreshingSignal ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                </Fab>
              </span>
            </Tooltip>
          </Box>
        </Panel>
      </ReactFlow>
      <Menu
        anchorEl={addMenuAnchor}
        open={Boolean(addMenuAnchor)}
        onClose={handleAddMenuClose}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        container={isFullscreen && container ? container : undefined}
        sx={{ zIndex: isFullscreen ? 10002 : 1400 }}
      >
        <MenuItem onClick={handleOpenOltSelection}>
          <ListItemIcon><OltIcon /></ListItemIcon>
          <ListItemText primary="Adicionar OLT" />
        </MenuItem>
        <MenuItem onClick={handleOpenSplitterModal}>
          <ListItemIcon><SplitterIcon /></ListItemIcon>
          <ListItemText primary="Splitter" secondary="1x2, 1x4, 1x8, 1x16 ou 1x32" />
        </MenuItem>
        <MenuItem onClick={handleOpenDioModal}>
          <ListItemIcon><DioIcon /></ListItemIcon>
          <ListItemText primary="DIO" secondary="Distribuidor Interno Óptico" />
        </MenuItem>
      </Menu>
      <DioAddModal
        open={showDioModal}
        onClose={() => setShowDioModal(false)}
        onConfirm={handleAddDio}
        isFullscreen={isFullscreen}
        container={container}
      />

      <SplitterAddModal
        open={showSplitterAddModal}
        onClose={() => setShowSplitterAddModal(false)}
        onConfirm={handleAddSplitter}
        isFullscreen={isFullscreen}
        container={container}
      />

      <SplitterEditPadraoModal
        open={showSplitterEditModal}
        onClose={() => {
          setShowSplitterEditModal(false);
          setEditingSplitter(null);
        }}
        onConfirm={handleSaveSplitterPadrao}
        padraoAtual={editingSplitter?.padrao_cores}
        splitterConfig={editingSplitter?.config}
        is1x2={editingSplitter?.is1x2}
        saida1Atual={editingSplitter?.saida1_porcentagem}
        saida2Atual={editingSplitter?.saida2_porcentagem}
        isFullscreen={isFullscreen}
        container={container}
      />

      <DioEditPadraoModal
        open={showDioEditModal}
        onClose={() => {
          setShowDioEditModal(false);
          setEditingDio(null);
        }}
        onConfirm={handleSaveDioPadrao}
        padraoAtual={editingDio?.padrao_cores}
        qtdePortas={editingDio?.qtde_portas}
        isFullscreen={isFullscreen}
        container={container}
      />

      <CaboSelectionModal
        open={showCaboSelectionModal}
        onClose={() => setShowCaboSelectionModal(false)}
        onSelectCabo={handleAddCabo}
        isFullscreen={isFullscreen}
        container={container}
      />

      <Dialog
        open={showCabosForm}
        onClose={handleCabosFormCancel}
        maxWidth="md"
        fullWidth
        container={isFullscreen && container ? container : undefined}
        BackdropProps={{ sx: { backgroundColor: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(4px)' } }}
        sx={{ zIndex: isFullscreen ? 10004 : 1500 }}
      >
        <DialogContent>
          <CabosForm
            cabo={editingCabo || undefined}
            pontos={(editingCabo?.pontos ?? []).map((ponto) => ({
              lat: ponto.lat,
              lng: ponto.lng,
              id: ponto.id,
              caixa_armario_id: ponto.caixa_armario_id,
              caixa_armario_tipo: ponto.caixa_armario_tipo,
            }))}
            menuZIndex={isFullscreen ? 10005 : 1600}
            onSuccess={handleCabosFormSuccess}
            onCancel={handleCabosFormCancel}
          />
        </DialogContent>
      </Dialog>

      {/* Modal de seleção de OLT */}
      <Dialog
        open={showOltSelectionModal}
        onClose={() => setShowOltSelectionModal(false)}
        maxWidth="sm"
        fullWidth
        container={isFullscreen && container ? container : undefined}
        BackdropProps={{ sx: { backgroundColor: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(4px)' } }}
        sx={{ zIndex: isFullscreen ? 10003 : 1500 }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <OltIcon />
            <Typography variant="h6">Selecionar OLT</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingOlts ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : oltsDisponiveis.length === 0 ? (
            <Alert severity="info">Nenhuma OLT cadastrada</Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
              {oltsDisponiveis.map((olt) => (
                <Paper
                  key={olt.id}
                  sx={{
                    p: 1.5,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': { bgcolor: '#e3f2fd', transform: 'translateY(-1px)', boxShadow: 2 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                  onClick={() => handleAddOlt(olt)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <OltIcon sx={{ color: '#1565c0' }} />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{olt.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{olt.model} | {olt.ip_address}</Typography>
                    </Box>
                  </Box>
                  <Chip
                    label={olt.vendor}
                    size="small"
                    color={olt.status === 'online' ? 'success' : olt.status === 'offline' ? 'error' : 'warning'}
                    variant="outlined"
                  />
                </Paper>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowOltSelectionModal(false)}>Cancelar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

ArmarioDiagramContent.displayName = 'ArmarioDiagramContent';

const ArmarioDiagramModal = React.memo<ArmarioDiagramModalProps>(({ open, onClose, armario, isFullscreen = false, container }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      container={container ?? undefined}
      BackdropProps={{ sx: { backgroundColor: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(4px)' } }}
      sx={{
        zIndex: isFullscreen ? 10000 : 1300,
        '& .MuiDialog-paper': { height: '80vh', maxHeight: '80vh', zIndex: isFullscreen ? 10001 : 'auto' },
        '& .MuiDialogContent-root': { py: 0 },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 1.5, pb: 1, px: 2 }}>
        <Box sx={{ '& .MuiTypography-root': { lineHeight: 1.3 } }}>
          <Typography variant="h6" sx={{ fontSize: '1rem', mb: 0.25 }}>
            📦 Diagrama do Armário - {armario.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            Documentação de fusões, splitters e conectividade
          </Typography>
        </Box>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ mb: 0.5, mr: 0.5, ml: 0.5, p: 0, pt: 0, overflow: 'hidden' }}>
        <ReactFlowProvider>
          <ArmarioDiagramContent armario={armario} onClose={onClose} isFullscreen={isFullscreen} container={container} />
        </ReactFlowProvider>
      </DialogContent>
    </Dialog>
  );
});

ArmarioDiagramModal.displayName = 'ArmarioDiagramModal';
export default ArmarioDiagramModal;
